import { useQueryClient, useQueryErrorResetBoundary } from "@tanstack/react-query";
import { useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import { USER_FACING_ERROR_TAGS } from "@/api/middleware/safe-error";
import { Forbidden } from "@/components/error-surfaces/forbidden";
import { MaintenanceScreen } from "@/components/maintenance-takeover";
import { ServerError } from "@/components/error-surfaces/server-error";
import { useFeatureFlag, useFeatureFlagPayload } from "@/hooks/feature-flags";
import { Button } from "@/components/ui/button";
import { BffUserFacingError, UNAUTHORIZED_MESSAGE, isUnauthorizedError } from "@/lib/errors";
import { loginWithReturnTo } from "@/lib/route-paths";

// Default surfaces for router error and pending states, registered in
// router.tsx (and as the root route's errorComponent). This module is the
// dispatch: it decides which branded surface a thrown error resolves to, and
// the surfaces themselves (components/error-surfaces/) know nothing about the
// wire.

// Dispatched on the status, not the tag. Python's Forbidden family appends the
// capability it gated on when it has one, so the tag arrives either bare
// (`role-forbidden`, the endpoint gate) or suffixed
// (`role-forbidden:<capability>`, a use-case gate) — server/app/core/errors.py.
// Matching the tag would mean prefix-matching it, and api/middleware/safe-error.ts
// documents the opposite policy for tags: exact-match, a base tag never covering
// its suffixed variants. The status carries no such ambiguity, and it is the
// surface's own identity: a 403 surface for a 403.
function isForbidden(error: unknown): error is BffUserFacingError {
  return error instanceof BffUserFacingError && error.status === 403;
}

function ErrorShell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center">
      <h1 className="text-2xl font-semibold">{title}</h1>
      {children}
    </div>
  );
}

export function DefaultErrorComponent({ error }: ErrorComponentProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const maintenance = useFeatureFlag("enable_maintenance");
  const maintenancePayload = useFeatureFlagPayload("enable_maintenance");
  // Reset the Query error boundary on mount, per the Router↔Query integration
  // doc: without it, "Try again" re-renders against the still-errored query
  // and fails instantly instead of refetching.
  const { reset } = useQueryErrorResetBoundary();
  useEffect(() => {
    reset();
  }, [reset]);

  // Maintenance outranks every error surface. The root errorComponent renders
  // *instead of* RootLayout (routes/__root.tsx), so this is the one render path
  // the takeover's Outlet wrap can't reach — and it is live exactly when
  // maintenance is: a window in which backing services are down is a window in
  // which loaders fail (#153, "every path renders the maintenance screen").
  if (maintenance) {
    return <MaintenanceScreen backAt={maintenancePayload?.back_at} />;
  }

  // Backstop only. An expired session is intercepted at the Query layer
  // (router.tsx) and answered by the session-expired overlay, which leaves the
  // route mounted and the visitor's in-progress edits intact; this branch is
  // what catches an unauthorized error thrown somewhere no query cache sees —
  // a render, or a loader outside the _authed gate — where the route is coming
  // down anyway and a full-page surface is the honest one. Retrying can't fix
  // a dead session either way, so it routes to sign-in and clears the cache so
  // the gate re-checks rather than passing on the stale current-user.
  if (isUnauthorizedError(error)) {
    return (
      <ErrorShell title="Session expired">
        <p className="text-lennar-grey-500 max-w-md text-sm">{UNAUTHORIZED_MESSAGE}</p>
        <Button
          onClick={() => {
            queryClient.clear();
            void router.navigate(loginWithReturnTo(router.state.location.href));
          }}
        >
          Sign in again
        </Button>
      </ErrorShell>
    );
  }

  // A BffUserFacingError with a whitelisted tag is a message Python wrote
  // FOR the user (the scrub middleware forwards exactly this set) — hiding
  // it in production would turn every trusted business error into a generic
  // retry screen. The tag re-check mirrors the scrub's allowlist because the
  // class alone isn't proof: it's constructed from any tagged Python error
  // body, whitelisted or not (internal-api/client.ts).
  const trustedMessage =
    error instanceof BffUserFacingError && USER_FACING_ERROR_TAGS.has(error.tag)
      ? error.message
      : undefined;

  // A permission wall, not a failure: it gets its own surface, with the request
  // that would lift the wall rather than a retry that cannot. `trustedMessage`
  // stays exact-match, so a suffixed tag reaches the 403 but names its
  // capability only once that exact tag is on the manifest allowlist.
  if (isForbidden(error)) {
    return <Forbidden message={trustedMessage} />;
  }

  return (
    <ServerError
      // Invalidate rather than reload the document: a router error is usually
      // one stale loader, and the Query error boundary was just reset above, so
      // re-running the loaders is the cheapest thing that can actually recover.
      onReload={() => {
        void router.invalidate();
      }}
      detail={
        <>
          {trustedMessage && <p className="text-lennar-grey-500 pt-1 text-sm">{trustedMessage}</p>}
          {/* Raw messages only in dev: errors thrown outside the server-function
              scrub path (loaders, render) may carry internals. Mirrors the
              router's built-in ErrorComponent, which hides them in production. */}
          {!trustedMessage && import.meta.env.DEV && (
            <pre className="bg-muted mt-2 max-w-full overflow-auto rounded-md p-4 text-left text-sm whitespace-pre-wrap">
              {error instanceof Error ? error.message : String(error)}
            </pre>
          )}
        </>
      }
    />
  );
}

export function DefaultPendingComponent() {
  return (
    <div
      className="text-lennar-grey-500 flex min-h-[60vh] items-center justify-center p-8"
      role="status"
    >
      Loading…
    </div>
  );
}
